import { h, Host } from "@stencil/core";
import { serializeForm, waitForHydrated } from "@trumpf/ux.utils";
import { throttle } from "../../utils/throttle";
import { elementsSelectors } from "./supported-elements-selectors";
import { scrollToTarget } from "../../utils/scrollToTarget";
import api from "../../utils/api";
import { getErrorMessageSetter, getFormData, getInputElements, getValidationAttributes, handleDisabledElements, validateFormGroup } from "./utils.js";
import { logger } from "../../utils/console";
/**
 * @slot (default) - The slot for the form element including all the input fields
 *                   that need to be validated
 */
export class FormValidator {
    constructor() {
        this.validationOnBlur = false;
        this.noValidation = false;
        this.async = false;
        this.additionalScrollOffset = 0;
        this.isSubmittingAsync = false;
        this.asyncError = false;
        this.customAction = null;
    }
    baseClass = "ux-form";
    formElement;
    /** this is an array of all supported ux form elements:
     * (HTMLUxInputFieldElement | HTMLUxSelectElement | [...])[]
     **/
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    uxInputElements;
    isValid = true;
    skipValidation = false;
    isValidPassword = false;
    isConfirmedPassword = false;
    dataPrefix = "data-validation";
    dataNovalidation = `${this.dataPrefix}-novalidation`;
    dataRequired = `${this.dataPrefix}-required`;
    dataInvalid = `${this.dataPrefix}-invalid`;
    dataLength = `${this.dataPrefix}-length`;
    dataValue = `${this.dataPrefix}-value`;
    dataGroup = `${this.dataPrefix}-group`;
    dataRegex = `${this.dataPrefix}-regex`;
    element;
    /** function that performs the validation of the input elements */
    async validate() {
        this.validateForm();
    }
    /** function that allows to validate just specific elements of the form */
    async validateElements(container) {
        const elementsToValidate = getInputElements({
            container: container,
            supportedElements: elementsSelectors
        });
        elementsToValidate.forEach((uxInputElement) => {
            this.validateField(uxInputElement);
        });
        this.scrollToFirstError({
            uxElements: elementsToValidate
        });
    }
    /**
     * An event emitted when the form has been validated,
     * if preventDefault is called on the event, the form will not submit,
     * even if there are no validation errors.
     * It contains the following keys: isValid, serialisedFormData, formData.
     */
    formValidationEvent;
    componentDidLoad() {
        this.initForm();
    }
    initForm = () => {
        // @ts-expect-error Check for typing
        this.formElement = this.element.querySelector("form");
        this.skipValidation = this.noValidation || this.formElement?.hasAttribute("novalidate");
        this.uxInputElements = getInputElements({
            container: this.element,
            supportedElements: elementsSelectors
        });
        this.addInputsEventListeners();
        if (!this.formElement) {
            return;
        }
        this.formElement.addEventListener("submit", this.validateForm);
    };
    passwordValidated(e) {
        this.isValidPassword = e.detail.success;
    }
    passwordConfirmed(e) {
        this.isConfirmedPassword = e.detail.success;
    }
    addInputsEventListeners = () => {
        this.uxInputElements.forEach((uxInputElement) => {
            waitForHydrated(uxInputElement, () => {
                const inputs = uxInputElement.querySelectorAll("input, select, textarea");
                if (inputs.length === 0) {
                    return;
                }
                inputs.forEach((input) => {
                    input.addEventListener("invalid", this.validateOnInvalid);
                    if (this.validationOnBlur) {
                        input.addEventListener("blur", this.validateOnBlur);
                    }
                });
            });
        });
    };
    validateForm = async (e = null) => {
        if (this.async) {
            this.asyncError = false;
        }
        if (this.skipValidation) {
            if (this.async && e !== null) {
                e?.preventDefault();
                await this.submitAsyncData();
            }
            return;
        }
        this.uxInputElements.forEach((uxInputElement) => {
            this.validateField(uxInputElement);
        });
        this.setIsValid();
        const formData = getFormData({
            uxElements: this.uxInputElements
        });
        const event = this.formValidationEvent.emit({
            isValid: this.isValid,
            // @ts-expect-error Check for typing
            serializedFormData: this.formElement ? serializeForm(this.formElement) : null,
            formData: formData
        });
        if (!this.isValid) {
            this.scrollToFirstError({
                uxElements: this.uxInputElements
            });
        }
        if (event.defaultPrevented) {
            // @ts-expect-error Check for typing
            e.preventDefault();
        }
        if (this.async && this.isValid && e !== null) {
            e?.preventDefault();
            await this.submitAsyncData();
        }
        if (!this.isValid && e !== null) {
            e.preventDefault();
        }
    };
    submitAsyncData = async () => {
        if (this.isSubmittingAsync) {
            return;
        }
        const url = this.customAction || this.formElement.action;
        try {
            this.isSubmittingAsync = true;
            const options = isMockUrl(url)
                ? {}
                : {
                    method: "POST",
                    body: serializeForm(this.formElement),
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
                    }
                };
            const response = await api(url, options);
            if (isMockUrl(url)) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
            this.isSubmittingAsync = false;
            this.element.innerHTML = "";
            this.element.insertAdjacentHTML("afterbegin", response);
            scrollToTarget({
                target: this.element,
                additionalOffsetTop: 30 + this.additionalScrollOffset
            });
            this.initForm();
        }
        catch (e) {
            this.isSubmittingAsync = false;
            this.asyncError = true;
            logger.error(`Failed to submit form data`, e);
        }
    };
    setIsValid = () => {
        this.isValid = !this.uxInputElements.find((uxInputElement) => uxInputElement.error === true &&
            !uxInputElement.hasAttribute(this.dataNovalidation) &&
            !uxInputElement.hasAttribute("disabled"));
    };
    resetError = (eTarget) => {
        if (eTarget.closest("ux-form") !== this.element) {
            return;
        }
        const formElement = eTarget.closest(`ux-form-group`) || eTarget.closest(elementsSelectors.join(", "));
        if (formElement === null) {
            return;
        }
        if (formElement.classList.contains("ux-file-upload")) {
            return;
        }
        /** formElement is an element of all supported ux form elements:
         * HTMLUxInputFieldElement | HTMLUxSelectElement | [...]
         **/
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        formElement.error = false;
    };
    instantValidation(e) {
        const eTarget = e.target;
        this.resetError(eTarget);
    }
    resetErrorStatusOnFocus(e) {
        const eTarget = e.target;
        this.resetError(eTarget);
    }
    validateFormThrottled = throttle(() => this.validateForm(), 300);
    validateOnInvalid = (e) => {
        e.preventDefault();
        this.validateFormThrottled();
    };
    validateOnBlur = (e) => {
        const eTarget = e.target;
        const uxInputElement = this.uxInputElements.find((uxElement) => uxElement.contains(eTarget));
        this.validateField(uxInputElement);
    };
    /** uxInputElement is an element of all supported ux form elements:
     * HTMLUxInputFieldElement | HTMLUxSelectElement | [...]
     **/
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    validateField = (uxInputElement) => {
        if (uxInputElement.hasAttribute(this.dataNovalidation) || uxInputElement.hasAttribute("disabled")) {
            return;
        }
        const inputElements = Array.from(uxInputElement.querySelectorAll("input, select, textarea"));
        const setErrorMessage = getErrorMessageSetter({
            uxInputElement: uxInputElement,
            uxForm: this.element
        });
        if (uxInputElement.classList.contains(`ux-form-group`)) {
            validateFormGroup({
                uxElement: uxInputElement,
                errorSetter: setErrorMessage,
                dataGroup: this.dataGroup,
                dataRequired: this.dataRequired
            });
        }
        const val = getValidationAttributes({
            uxElement: uxInputElement,
            dataLength: this.dataLength,
            dataValue: this.dataValue,
            dataRegex: this.dataRegex
        });
        inputElements.forEach((inputEl) => {
            const value = inputEl.value;
            if (uxInputElement.required === true && inputEl.validity.valueMissing) {
                setErrorMessage(`${this.dataRequired}--msg`);
            }
            else if (inputEl.validity.typeMismatch || inputEl.validity.badInput) {
                setErrorMessage(`${this.dataInvalid}--msg`);
            }
            else if (inputEl.validity.patternMismatch) {
                setErrorMessage(`${this.dataInvalid}--msg`);
            }
            else if (val.minLength !== null && value.length < val.minLength) {
                setErrorMessage(`${this.dataLength}--msg`);
            }
            else if (val.maxLength !== null && value.length > val.maxLength) {
                setErrorMessage(`${this.dataLength}--msg`);
            }
            else if (val.minValue !== null && Number(value) < val.minValue) {
                setErrorMessage(`${this.dataValue}--msg`);
                // @ts-expect-error Check for typing
            }
            else if (val.minValue !== null && Number(value) > val.maxValue) {
                setErrorMessage(`${this.dataValue}--msg`);
            }
            else if (uxInputElement.required === true &&
                inputEl.getAttribute("data-password") === "true" &&
                !this.isValidPassword) {
                setErrorMessage(`${this.dataRequired}--msg`);
            }
            else if (uxInputElement.required === true &&
                inputEl.hasAttribute("data-password-confirm") &&
                !this.isConfirmedPassword) {
                setErrorMessage(`${this.dataRequired}--msg`);
            }
            else if (val.regexExpression !== null) {
                let regex;
                if (val.regexFlag !== null) {
                    regex = new RegExp(val.regexExpression, val.regexFlag);
                }
                else {
                    regex = new RegExp(val.regexExpression);
                }
                if (!value.match(regex)) {
                    setErrorMessage(`${this.dataRegex}--msg`);
                }
            }
            else if (!inputEl.validity.valid) {
                setErrorMessage(null);
            }
        });
    };
    /** AVOIDS VALIDATION ON HIDDEN ELEMENTS */
    handleFilterEvent(e) {
        const eTarget = e.target;
        if (!this.element.contains(eTarget)) {
            return;
        }
        const { htmlElement, hidden } = e.detail;
        handleDisabledElements(htmlElement, elementsSelectors, hidden);
    }
    /** scrolls form to first error */
    scrollToFirstError = ({ uxElements }) => {
        const firstElementWithError = uxElements.find((uxInputElement) => {
            return uxInputElement.error;
        });
        if (!firstElementWithError) {
            return;
        }
        const elementTop = firstElementWithError.getBoundingClientRect().top;
        const isInViewport = elementTop < 0;
        if (isInViewport) {
            scrollToTarget({
                target: firstElementWithError,
                additionalOffsetTop: 10 + this.additionalScrollOffset
            });
        }
    };
    handleSubmitButtonAttributes(e) {
        const eTarget = e.target;
        if (!this.element.contains(eTarget)) {
            return;
        }
        if (eTarget.hasAttribute("formnovalidate")) {
            this.skipValidation = true;
        }
        else {
            this.skipValidation = this.noValidation || this.formElement?.hasAttribute("novalidate");
        }
        if (eTarget.hasAttribute("formaction")) {
            this.customAction = eTarget.getAttribute("formaction");
        }
        else {
            this.customAction = null;
        }
    }
    render() {
        return (h(Host, { key: '57813683486944a65defa81447447d6af6789b0b', class: this.baseClass }, h("slot", { key: 'cd4b4809d59c3087ea7a6ce2666c1c635b84fa74' }), this.async && this.isSubmittingAsync && h("ux-spinner-overlay", { key: 'f85e0a6c25d14f0545028375882a410f41c78f39', opened: true })));
    }
    static get is() { return "ux-form"; }
    static get originalStyleUrls() {
        return {
            "$": ["form.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["form.css"]
        };
    }
    static get properties() {
        return {
            "validationOnBlur": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the validation will occur on element blur"
                },
                "attribute": "validation-on-blur",
                "reflect": false,
                "defaultValue": "false"
            },
            "noValidation": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the validation will not occour"
                },
                "attribute": "no-validation",
                "reflect": false,
                "defaultValue": "false"
            },
            "async": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the form method will be ignored, instead,\nthe formData will be sent asynchronously to the url specified in the\nform action attribute"
                },
                "attribute": "async",
                "reflect": false,
                "defaultValue": "false"
            },
            "additionalScrollOffset": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "an additional ofset top amout for the automatic form scerollings"
                },
                "attribute": "additional-scroll-offset",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get states() {
        return {
            "isSubmittingAsync": {},
            "asyncError": {},
            "customAction": {}
        };
    }
    static get events() {
        return [{
                "method": "formValidationEvent",
                "name": "uxFormValidated",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "An event emitted when the form has been validated,\nif preventDefault is called on the event, the form will not submit,\neven if there are no validation errors.\nIt contains the following keys: isValid, serialisedFormData, formData."
                },
                "complexType": {
                    "original": "{\n        isValid: boolean;\n        serializedFormData: string;\n        formData: object;\n    }",
                    "resolved": "{ isValid: boolean; serializedFormData: string; formData: object; }",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "validate": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "function that performs the validation of the input elements",
                    "tags": []
                }
            },
            "validateElements": {
                "complexType": {
                    "signature": "(container: HTMLElement) => Promise<void>",
                    "parameters": [{
                            "name": "container",
                            "type": "HTMLElement",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "function that allows to validate just specific elements of the form",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "element"; }
    static get listeners() {
        return [{
                "name": "uxPasswordValidated",
                "method": "passwordValidated",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "uxPasswordConfirmed",
                "method": "passwordConfirmed",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "change",
                "method": "instantValidation",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "focusin",
                "method": "resetErrorStatusOnFocus",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "uxStaticFilter",
                "method": "handleFilterEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "click",
                "method": "handleSubmitButtonAttributes",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
function isMockUrl(url) {
    return url.includes("mock-data/") || url.includes("Assets/fetchContent");
}
//# sourceMappingURL=form.js.map
